import React, { useState, useEffect } from 'react';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useMaintenance } from '../contexts/MaintenanceContext';
import { maintenanceService } from '../services/maintenanceService';
import { apiStatusManager } from '../services/ApiStatusManager';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTools, faClock } from '@/icons';

/**
 * Maintenance page displayed when maintenance mode is active or API returns 503
 */
export const MaintenancePage: React.FC = () => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { maintenanceConfig, timeUntilNextCheck, refresh } = useMaintenance();
    const [isRetrying, setIsRetrying] = useState(false);
    const [timeUntilEnd, setTimeUntilEnd] = useState<number | null>(null);

    const containerStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
        backgroundColor: theme.colors.background,
        color: theme.colors.text.primary,
    };

    const iconStyle = {
        fontSize: '4rem',
        marginBottom: '2rem',
        color: theme.colors.primary,
    } as React.CSSProperties;

    const titleStyle: React.CSSProperties = {
        ...themedStyles.text.heading,
        fontSize: '2rem',
        marginBottom: '1rem',
    };

    const messageStyle: React.CSSProperties = {
        ...themedStyles.text.body,
        fontSize: '1.2rem',
        marginBottom: '1rem',
        opacity: 0.8,
        maxWidth: '600px',
    };

    const infoStyle: React.CSSProperties = {
        ...themedStyles.text.body,
        fontSize: '1rem',
        marginTop: '0.5rem',
        opacity: 0.7,
        maxWidth: '600px',
    };

    const countdownStyle: React.CSSProperties = {
        ...themedStyles.text.body,
        fontSize: '0.9rem',
        marginTop: '1rem',
        opacity: 0.6,
        fontStyle: 'italic',
    };

    // Update time until end every second
    useEffect(() => {
        const updateTimeUntilEnd = () => {
            const timeUntil = maintenanceService.getTimeUntilEnd(maintenanceConfig);
            setTimeUntilEnd(timeUntil);
        };

        updateTimeUntilEnd();
        const interval = setInterval(updateTimeUntilEnd, 1000);

        return () => clearInterval(interval);
    }, [maintenanceConfig]);

    // Format milliseconds to human-readable time
    const formatTime = (ms: number): string => {
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);

        if (hours > 0) {
            return `${hours}h ${minutes % 60}m`;
        } else if (minutes > 0) {
            return `${minutes}m ${seconds % 60}s`;
        } else {
            return `${seconds}s`;
        }
    };

    // Format countdown timer (seconds only)
    const formatCountdown = (ms: number): string => {
        const seconds = Math.ceil(ms / 1000);
        return `${seconds}s`;
    };

    const handleTryAgain = async () => {
        setIsRetrying(true);
        try {
            // Refresh maintenance status
            await refresh();
            // Also try API retry if it was an API maintenance
            await apiStatusManager.retryLastRequest();
            // If successful, the API status will be updated and the page will disappear
            // If it fails, we'll catch the error and stay on the maintenance page
        } catch (error) {
            // Request still failing, stay on maintenance page
            console.warn('Retry failed:', error);
        } finally {
            setIsRetrying(false);
        }
    };

    // Use custom message if available
    const message = maintenanceConfig?.message || 
        "We're working to improve Calculist and will be back online shortly. Please check back in a few minutes.";

    // Format expected end time
    const expectedEndTime = maintenanceConfig?.endedAt 
        ? new Date(maintenanceConfig.endedAt).toLocaleString(undefined, {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short'
          })
        : null;

    const buttonStyle: React.CSSProperties = {
        ...themedStyles.button.primary,
        ...(isRetrying ? themedStyles.button.disabled : {}),
        marginTop: theme.spacing.md,
    };

    return (
        <div style={containerStyle}>
            <FontAwesomeIcon icon={faTools} style={iconStyle as any} />
            <h1 style={titleStyle}>Calculist is Currently Undergoing Maintenance</h1>
            <p style={messageStyle}>{message}</p>
            
            {expectedEndTime && (
                <div style={infoStyle}>
                    <FontAwesomeIcon icon={faClock} style={{ marginRight: '0.5rem' }} />
                    Expected to be available again: <strong>{expectedEndTime}</strong>
                </div>
            )}

            {timeUntilEnd !== null && timeUntilEnd > 0 && (
                <div style={infoStyle}>
                    Estimated time remaining: <strong>{formatTime(timeUntilEnd)}</strong>
                </div>
            )}

            <div style={countdownStyle}>
                Next check in: {formatCountdown(timeUntilNextCheck)}
            </div>

            <button
                onClick={handleTryAgain}
                disabled={isRetrying}
                style={buttonStyle}
            >
                {isRetrying ? 'Checking...' : 'Check Again'}
            </button>
        </div>
    );
};
